import React, { useRef, useState } from 'react';
import { Formik } from 'formik';
import html2canvas from 'html2canvas';

import { Button } from '@/ui-kit/button/button';
import { Icon } from '@/ui-kit/icon/icon';
import { Input } from '@/forms/components';
import { FormValidationSchemaBuilder } from '@/forms/builders';
import { trimCanvas } from '@/ui-kit/signature/trimCanvas';

import './SignatureGenerator.scss';

const generateImage = async (element: HTMLElement | null) => {
  if (!element) {
    return;
  }

  const canvas = await html2canvas(element, {
    backgroundColor: null,
  });
  return trimCanvas(canvas, { paddingPx: 12 }).toDataURL('image/png', 1.0);
};

const initialValues = {
  signature: '',
};

const validationsSchema = FormValidationSchemaBuilder.object().shape({
  signature: FormValidationSchemaBuilder.string()
    .matches(/^[a-zA-Z.\s]*$/, 'Only letters, periods and spaces are allowed')
    .max(50)
    .required('Your name is required'),
});

interface SignatureGeneratorProps {
  onSignatureGenerated: (signature: string) => void;
}

export const SignatureGenerator: React.FC<SignatureGeneratorProps> = ({
  onSignatureGenerated,
}) => {
  const signatureRef = useRef<HTMLElement>(null);

  const [image, setImage] = useState<string | undefined>();

  const onSubmit = async () => {
    const imageUrl = await generateImage(signatureRef.current);
    setImage(imageUrl);
    if (imageUrl !== undefined) {
      onSignatureGenerated(imageUrl);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validationsSchema}
    >
      {({ handleSubmit, values, isValid, submitForm }) => {
        const value = values.signature;
        const signature = signatureRef.current as HTMLElement;
        if (signature && isValid && value !== '') {
          signature.textContent = value;
        } else if (signature) {
          signature.textContent = 'Signature';
        }

        return (
          <form
            autoComplete="off"
            className="signature-generator__container"
            onSubmit={handleSubmit}
          >
            <div className="signature-generator__input-container">
              <Input.Text
                name="signature"
                label="Your Signature"
                leadingIcon={<Icon type="quill" size="large" />}
                required
              />
              <Button
                type="button"
                onClick={submitForm}
                variant="secondary"
                kind="default"
                className="signature-generator__button"
              >
                Transform to Signature
              </Button>
            </div>
            <span
              ref={signatureRef}
              className="signature-generator__hidden-signature"
            >
              Signature
            </span>
            <div className="signature-generator__canvas">
              {image && <img src={image} alt="Siganture capture" />}
            </div>
          </form>
        );
      }}
    </Formik>
  );
};
